import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export function useCollapsibleStrip(storageKey, defaultExpanded = true) {
  const [expanded, setExpanded] = useState(() => {
    if (!storageKey) return defaultExpanded;
    try {
      const v = sessionStorage.getItem(storageKey);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch (_) {
      /* ignore */
    }
    return defaultExpanded;
  });

  const persist = useCallback(
    (next) => {
      if (!storageKey) return;
      try {
        sessionStorage.setItem(storageKey, next ? '1' : '0');
      } catch (_) {
        /* ignore */
      }
    },
    [storageKey]
  );

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      persist(next);
      return next;
    });
  }, [persist]);

  const setExpandedPersist = useCallback(
    (next) => {
      setExpanded(next);
      persist(next);
    },
    [persist]
  );

  return { expanded, toggle, setExpanded: setExpandedPersist };
}

/**
 * Collapsible panel: expanded body + thin collapsed bar (title, optional summary, expand).
 */
export default function CollapsibleStrip({
  storageKey,
  defaultExpanded = true,
  /** When this value changes, the strip expands (e.g. after Load recipe). */
  expandSignal,
  title,
  subtitle,
  collapsedSummary,
  headerActions,
  children,
  className = 'overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm',
  headerClassName = 'flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-2',
  titleClassName = 'text-xs font-semibold uppercase tracking-wide text-slate-600',
  subtitleClassName = 'mt-0.5 text-[11px] text-slate-500',
  collapsedClassName = 'flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/90 px-4 py-1.5',
}) {
  const { expanded, toggle, setExpanded } = useCollapsibleStrip(storageKey, defaultExpanded);

  useEffect(() => {
    if (expandSignal == null || expandSignal === 0) return;
    setExpanded(true);
  }, [expandSignal, setExpanded]);

  const toggleBtn = (
    <button
      type="button"
      onClick={toggle}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100"
      aria-expanded={expanded}
      title={expanded ? 'Minimize' : 'Expand'}
    >
      {expanded ? (
        <>
          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          Minimize
        </>
      ) : (
        <>
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          Expand
        </>
      )}
    </button>
  );

  if (!expanded) {
    return (
      <div className={collapsedClassName}>
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {collapsedSummary ? (
          <span className="min-w-0 flex-1 truncate text-[11px] text-slate-500">{collapsedSummary}</span>
        ) : null}
        <span className="ml-auto">{toggleBtn}</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className={headerClassName}>
        <div className="min-w-0 flex-1">
          <div className={titleClassName}>{title}</div>
          {subtitle ? <p className={subtitleClassName}>{subtitle}</p> : null}
        </div>
        {headerActions ? <div className="flex flex-wrap items-center gap-1.5">{headerActions}</div> : null}
        {toggleBtn}
      </div>
      {children}
    </div>
  );
}
